/**
 * ClaimDiffPanel — reusable before/after diff panel for any Stage 3
 * claim mutation that follows the "server returns preview → client
 * renders → user accepts or discards" pattern. Used by:
 *
 *   - Narrow / Broaden  (no dependents — affectedDependents=[])
 *   - Regenerate        (no dependents — affectedDependents=[])
 *   - Steer             (dependents shown with accept checkboxes)
 *
 * The dependents block self-hides when affectedDependents is empty so
 * the same component drives all flows without flow-specific branching.
 *
 * Side-by-side on viewports ≥ 1024px, stacked below that — keeps the
 * panel usable inside the claim card on laptop screens.
 */
import React, { useState } from 'react';
import type { SteerAffectedDependent } from '@/api/client';

export interface ClaimDiffPanelProps {
  /** Pre-mutation text shown on the left ("Before"). */
  original: string;
  /** Post-mutation proposed text shown on the right ("After"). */
  modified: string;
  /** One-sentence summary the LLM emitted; rendered below the diff. */
  changeSummary?: string;
  /**
   * Dependent claims the mutation may affect. Each carries a
   * suggested rewrite the user can accept independently via checkbox.
   * Pass [] for flows without dependent suggestions (Narrow / Broaden /
   * Regenerate).
   */
  affectedDependents?: SteerAffectedDependent[];
  /**
   * Fires on Apply. Receives the list of dependent IDs whose
   * checkboxes were ticked. The caller is responsible for the actual
   * server call.
   */
  onApply: (acceptedDependentIds: string[]) => void;
  /** Fires on Discard. The caller clears state; no server call. */
  onDiscard: () => void;
  /** Apply-button copy. Defaults to "Apply changes". */
  applyLabel?: string;
  /** Stable test-id prefix — e.g. ``regen-preview-3`` or ``steer-preview-3``. */
  testIdPrefix: string;
  /**
   * When true, the Apply button is disabled (e.g. while a request is
   * in flight). Discard stays enabled.
   */
  applyPending?: boolean;
  /**
   * Optional note rendered above the buttons. Called with the ticked
   * dependent IDs so it can follow the checkboxes (Steer's antecedent
   * warning drops a dependent once its suggested update is ticked).
   */
  renderNotice?: (acceptedDependentIds: string[]) => React.ReactNode;
}

/**
 * Visual variant — light theme, white card with subtle borders. Matches
 * the existing Stage 3 claim-card visual language (#002FA7 primary,
 * IBM Plex Serif claim text).
 */
export function ClaimDiffPanel({
  original,
  modified,
  changeSummary,
  affectedDependents,
  onApply,
  onDiscard,
  applyLabel = 'Apply changes',
  testIdPrefix,
  applyPending = false,
  renderNotice,
}: ClaimDiffPanelProps): JSX.Element {
  const deps = affectedDependents ?? [];
  const hasDeps = deps.length > 0;
  // Track which dependent suggestions the drafter has accepted. Default
  // unchecked — the drafter must explicitly opt in to each dependent
  // rewrite. This is the "don't apply something the user didn't see"
  // safety net documented in the design.
  const [acceptedDepIds, setAcceptedDepIds] = useState<Set<string>>(new Set());

  const toggleDep = (id: string) => {
    setAcceptedDepIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleApply = () => onApply(Array.from(acceptedDepIds));

  return (
    <div
      data-testid={`${testIdPrefix}-panel`}
      className="mx-4 mb-4 border border-blue-200 rounded-sm bg-blue-50/30 p-4"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-medium text-blue-800">
          Review proposed change
        </span>
        {changeSummary && (
          <span
            className="text-xs text-blue-700 italic ml-3 flex-1 text-right"
            data-testid={`${testIdPrefix}-summary`}
          >
            {changeSummary}
          </span>
        )}
      </div>

      {/* Before / After — side-by-side on wide screens, stacked below 1024px */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 font-mono">
            Before
          </div>
          <div
            data-testid={`${testIdPrefix}-before`}
            className="text-sm text-zinc-600 bg-white border border-zinc-200 rounded-sm p-3 whitespace-pre-wrap leading-relaxed"
            style={{ fontFamily: 'IBM Plex Serif, serif' }}
          >
            {original}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-blue-700 mb-1 font-mono">
            After
          </div>
          <div
            data-testid={`${testIdPrefix}-after`}
            className="text-sm text-zinc-900 bg-white border border-blue-300 rounded-sm p-3 whitespace-pre-wrap leading-relaxed"
            style={{ fontFamily: 'IBM Plex Serif, serif' }}
          >
            {modified}
          </div>
        </div>
      </div>

      {/* Dependent suggestions — only rendered when present */}
      {hasDeps && (
        <div
          className="mt-4 pt-3 border-t border-blue-200"
          data-testid={`${testIdPrefix}-deps`}
        >
          <div className="text-xs text-amber-800 font-medium mb-2">
            ⚠ {deps.length} dependent claim{deps.length === 1 ? '' : 's'} may need updating
          </div>
          <div className="space-y-3">
            {deps.map((d) => (
              <div
                key={d.id}
                className="border border-amber-200 bg-white rounded-sm p-3"
                data-testid={`${testIdPrefix}-dep-${d.claim_number}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="font-mono text-xs font-semibold text-zinc-800">
                    Claim {d.claim_number}
                  </span>
                  <label
                    className="text-xs flex items-center gap-1.5 text-zinc-700 cursor-pointer select-none"
                    data-testid={`${testIdPrefix}-dep-${d.claim_number}-accept-label`}
                  >
                    <input
                      type="checkbox"
                      checked={acceptedDepIds.has(d.id)}
                      onChange={() => toggleDep(d.id)}
                      data-testid={`${testIdPrefix}-dep-${d.claim_number}-checkbox`}
                      className="h-3.5 w-3.5"
                    />
                    Accept suggested update
                  </label>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 font-mono">
                      Current
                    </div>
                    <div
                      className="text-xs text-zinc-600 bg-zinc-50 border border-zinc-200 rounded-sm p-2 whitespace-pre-wrap"
                      style={{ fontFamily: 'IBM Plex Serif, serif' }}
                    >
                      {d.original_text}
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-blue-700 mb-1 font-mono">
                      Suggested
                    </div>
                    <div
                      className="text-xs text-zinc-900 bg-blue-50/50 border border-blue-200 rounded-sm p-2 whitespace-pre-wrap"
                      style={{ fontFamily: 'IBM Plex Serif, serif' }}
                    >
                      {d.suggested_text}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {renderNotice?.(Array.from(acceptedDepIds))}

      {/* Actions */}
      <div className="flex gap-2 mt-4">
        <button
          data-testid={`${testIdPrefix}-apply`}
          onClick={handleApply}
          disabled={applyPending}
          className="bg-[#002FA7] text-white hover:bg-[#002277] disabled:opacity-60 disabled:cursor-not-allowed rounded-sm px-3 py-1.5 text-xs font-medium transition-colors"
        >
          {applyLabel}
        </button>
        <button
          data-testid={`${testIdPrefix}-discard`}
          onClick={onDiscard}
          disabled={applyPending}
          className="bg-white text-zinc-700 border border-zinc-300 hover:bg-zinc-50 disabled:opacity-60 disabled:cursor-not-allowed rounded-sm px-3 py-1.5 text-xs font-medium transition-colors"
        >
          Discard
        </button>
      </div>
    </div>
  );
}

export default ClaimDiffPanel;
